import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Search, ShoppingCart, User, Store } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/hooks/use-cart";
import { useSession } from '@supabase/auth-helpers-react';
import { cn } from "@/lib/utils";

const navItems = [
  { icon: Home, label: "Home", href: "/" },
  { icon: Search, label: "Search", href: "/search" },
  { icon: Store, label: "Shop", href: "/shop" },
  { icon: ShoppingCart, label: "Cart", href: "/cart" },
  { icon: User, label: "Profile", href: "/profile" },
];

export const MobileBottomNav = () => {
  const location = useLocation();
  const { items: cartItems } = useCart();
  const session = useSession();

  // Hide on specific pages where it's not needed
  const hiddenPaths = ['/admin', '/customer/login'];
  const isHidden = hiddenPaths.some(path => location.pathname.startsWith(path));

  if (isHidden) return null;

  return (
    <motion.nav
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.href;
          const Icon = item.icon;
          
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "relative flex flex-col items-center justify-center min-w-[60px] py-1 px-2 rounded-lg transition-all duration-200",
                "active:scale-95 touch-manipulation",
                isActive 
                  ? "text-primary-deep" 
                  : "text-gray-500 hover:text-primary-deep"
              )}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative"
              >
                <Icon className="h-6 w-6" />
                
                {/* Cart badge */}
                {item.label === "Cart" && cartItems.length > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                  >
                    {cartItems.length}
                  </Badge>
                )}
              </motion.div>
              
              <span className="text-xs mt-1 font-medium">
                {item.label}
              </span>
              
              {/* Active indicator */}
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute -top-1 left-1/2 w-1 h-1 bg-primary rounded-full"
                  style={{ x: '-50%' }}
                />
              )}
            </Link>
          );
        })}
      </div>
    </motion.nav>
  );
};